import React from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { SessionExpired } from "./SessionExpired";

interface AsyncResultProps<T, E extends Error> {
  query: UseQueryResult<T, E>;
  loading?: () => React.ReactNode;
  error?: (error: E) => React.ReactNode;
  fallback?: () => React.ReactNode;
  children: (data: T) => React.ReactNode;
  /** When true, errors will only be logged without showing error UI */
  suppressErrors?: boolean;
}


export function AsyncResult<T, E extends Error>({
  query,
  loading,
  error,
  fallback,
  children,
  suppressErrors = false,
}: AsyncResultProps<T, E>) {
  if (query.isLoading) {
    return <>{loading ? loading() : <></>}</>;
  }

  if (query.isError) {
    // Always log the error
    console.error("AsyncResult error:", query.error);

    // If errors are suppressed, render nothing
    if (suppressErrors) {
      return null;
    }

    // If custom error handler is provided, use it
    if (error) {
      return error(query.error);
    }

    // Default error handling
    if (query.error instanceof AxiosError) {
      switch (query.error.response?.status) {
        case 401:
          return <SessionExpired />;
        case 404:
          return <NotFound />;
        default:
          return query.error.message;
      }
    }
    return query.error.message;
  }

  if (query.data) {
    return children(query.data);
  }

  return <>{fallback ? fallback() : <NotFound />}</>;
}

function NotFound() {
  return (
    <>
      Not found <Link to="/">Home</Link>
    </>
  );
}
